import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collectGroups, TreeTableNode } from "./treeTableModel";

export interface TreeExpansion {
  /** Ids of collapsed groups. Groups not in the set are expanded. */
  collapsed: ReadonlySet<string>;
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
  collapseAll: () => void;
  expandAll: () => void;
  /** True when there is at least one group and every group is collapsed. */
  allCollapsed: boolean;
  /** True when no group is collapsed. */
  allExpanded: boolean;
  /** False for a flat (ungrouped) tree — hide Collapse/Expand all then. */
  hasGroups: boolean;
}

export interface UseTreeExpansionOptions {
  /** Decide whether a group starts collapsed the first time it appears
   *  (e.g. collapse the lowest level). Defaults to everything expanded. */
  initiallyCollapsed?: (node: TreeTableNode, depth: number) => boolean;
}

/**
 * Expansion state for GroupedTreeTable, lifted into a hook so the page's
 * action row can drive "Collapse all / Expand all" while the table renders.
 * Pass the result to `<GroupedTreeTable expansion={…} />`.
 *
 * Groups are expanded unless collapsed; `initiallyCollapsed` is applied once
 * per group id, the first time that id is seen, so async data and re-runs
 * keep the user's manual toggles.
 */
export function useTreeExpansion(
  nodes: TreeTableNode[],
  { initiallyCollapsed }: UseTreeExpansionOptions = {},
): TreeExpansion {
  const groups = useMemo(() => collectGroups(nodes), [nodes]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const seen = useRef<Set<string>>(new Set());
  const initialRef = useRef(initiallyCollapsed);
  initialRef.current = initiallyCollapsed;

  useEffect(() => {
    const fresh = groups.filter((g) => !seen.current.has(g.id));
    if (fresh.length === 0) return;
    fresh.forEach((g) => seen.current.add(g.id));
    const decide = initialRef.current;
    if (!decide) return;
    const toCollapse = fresh.filter((g) => decide(g.node, g.depth));
    if (toCollapse.length === 0) return;
    setCollapsed((prev) => {
      const next = new Set(prev);
      toCollapse.forEach((g) => next.add(g.id));
      return next;
    });
  }, [groups]);

  const toggle = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setCollapsed(new Set(groups.map((g) => g.id)));
  }, [groups]);

  const expandAll = useCallback(() => setCollapsed(new Set()), []);

  const hasGroups = groups.length > 0;
  const allCollapsed = hasGroups && groups.every((g) => collapsed.has(g.id));
  const allExpanded = groups.every((g) => !collapsed.has(g.id));

  return {
    collapsed,
    isOpen: (id: string) => !collapsed.has(id),
    toggle,
    collapseAll,
    expandAll,
    allCollapsed,
    allExpanded,
    hasGroups,
  };
}
